import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Trophy, Medal, Award, Target, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const RankingDisplay = ({ 
  rankings = null,
  showHistory = false,
  historicalRankings = []
}) => {
  const [viewMode, setViewMode] = React.useState('overall'); // overall, subjects

  if (!rankings) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Trophy className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Ranking Data</h3>
          <p className="text-gray-600">Rankings will appear here once they are calculated.</p>
        </CardContent>
      </Card>
    );
  }

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-5 h-5 text-yellow-600" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Award className="w-5 h-5 text-orange-600" />;
    return <Target className="w-4 h-4 text-gray-500" />;
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    if (rank === 2) return 'bg-gray-100 text-gray-800 border-gray-200';
    if (rank === 3) return 'bg-orange-100 text-orange-800 border-orange-200';
    if (rank <= 5) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (rank <= 10) return 'bg-green-100 text-green-800 border-green-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getRankText = (rank) => {
    if (rank === 1) return '1st';
    if (rank === 2) return '2nd';
    if (rank === 3) return '3rd';
    return `${rank}th`;
  };

  const getTrendIcon = (current, previous) => {
    if (!previous) return <Minus className="w-4 h-4 text-gray-500" />;
    if (current < previous) return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (current > previous) return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };


  const getPerformanceLevel = (rank, total) => {
    const percentage = (rank / total) * 100;
    if (percentage <= 10) return { level: 'Excellent', color: 'text-green-600' };
    if (percentage <= 25) return { level: 'Very Good', color: 'text-blue-600' };
    if (percentage <= 50) return { level: 'Good', color: 'text-yellow-600' };
    if (percentage <= 75) return { level: 'Average', color: 'text-orange-600' };
    return { level: 'Below Average', color: 'text-red-600' };
  };

  const performanceLevel = rankings.overall ? 
    getPerformanceLevel(rankings.overall.rank, rankings.overall.totalStudents) : 
    { level: 'N/A', color: 'text-gray-600' };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Academic Rankings</h3>
          <p className="text-sm text-gray-600">Your position in class and subject rankings</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'overall' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('overall')}
          >
            Overall
          </Button>
          <Button
            variant={viewMode === 'subjects' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('subjects')}
          >
            Subjects
          </Button>
        </div>
      </div>

      {/* Overall Ranking */}
      {viewMode === 'overall' && rankings.overall && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Main Ranking Card */}
          <Card className="border-2 border-blue-200">
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center mb-4">
                {getRankIcon(rankings.overall.rank)}
              </div>
              
              <div className="space-y-2">
                <Badge className={`text-lg px-4 py-2 ${getRankColor(rankings.overall.rank)}`}>
                  {getRankText(rankings.overall.rank)} Place
                </Badge>
                
                <p className="text-sm text-gray-600">
                  out of {rankings.overall.totalStudents} students
                </p>
                
                <div className="flex items-center justify-center gap-2">
                  <span className="text-sm text-gray-600">Performance:</span>
                  <span className={`font-medium ${performanceLevel.color}`}>
                    {performanceLevel.level}
                  </span>
                </div>
                
                <div className="text-2xl font-bold text-blue-600">
                  {rankings.overall.gpa.toFixed(2)} GPA
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ranking Details */}
          <Card>
            <CardContent className="p-6">
              <h4 className="font-semibold mb-4">Ranking Details</h4>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Current Rank</span>
                  <div className="flex items-center gap-2">
                    {getRankIcon(rankings.overall.rank)}
                    <span className="font-medium">{getRankText(rankings.overall.rank)}</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Students</span>
                  <span className="font-medium">{rankings.overall.totalStudents}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">GPA</span>
                  <span className="font-medium">{rankings.overall.gpa.toFixed(2)}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Percentile</span>
                  <span className="font-medium">
                    {Math.round(((rankings.overall.totalStudents - rankings.overall.rank + 1) / rankings.overall.totalStudents) * 100)}th
                  </span>
                </div>
              </div>

              {/* Historical Trend */}
              {showHistory && historicalRankings.length > 0 && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <h5 className="font-medium mb-3">Ranking Trend</h5>
                  <div className="space-y-2">
                    {historicalRankings.slice(-3).map((ranking, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{ranking.term}</span>
                        <div className="flex items-center gap-2">
                          {getTrendIcon(ranking.rank, historicalRankings[index - 1]?.rank)}
                          <span className="font-medium">{getRankText(ranking.rank)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Subject Rankings */}
      {viewMode === 'subjects' && rankings.subjects && (
        <div className="space-y-4">
          <Card>
            <CardContent className="p-6">
              <h4 className="font-semibold mb-4">Subject-wise Rankings</h4>
              
              <div className="space-y-3">
                {rankings.subjects.map((subject, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {getRankIcon(subject.rank)}
                      <div>
                        <p className="font-medium">{subject.subjectName}</p>
                        <p className="text-sm text-gray-600">
                          {subject.percentage.toFixed(1)}% • {subject.grade}
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <Badge className={getRankColor(subject.rank)}>
                        {getRankText(subject.rank)}
                      </Badge>
                      <p className="text-xs text-gray-500 mt-1">
                        of {subject.totalStudents} students
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Subject Performance Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {rankings.subjects.filter(s => s.rank <= 5).length}
                </div>
                <p className="text-sm text-gray-600">Top 5 Rankings</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {rankings.subjects.filter(s => s.rank <= 10).length}
                </div>
                <p className="text-sm text-gray-600">Top 10 Rankings</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {(rankings.subjects.reduce((sum, s) => sum + s.percentage, 0) / rankings.subjects.length).toFixed(1)}%
                </div>
                <p className="text-sm text-gray-600">Average Score</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Ranking Insights */}
      <Card>
        <CardContent className="p-6">
          <h4 className="font-semibold mb-4">Ranking Insights</h4>
          
          <div className="space-y-3">
            {rankings.overall && rankings.overall.rank <= 5 && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <Trophy className="w-5 h-5 text-green-600" />
                <span className="text-green-800">
                  Excellent performance! You're in the top 5 students in your class.
                </span>
              </div>
            )}
            
            {rankings.overall && rankings.overall.rank <= 10 && rankings.overall.rank > 5 && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Medal className="w-5 h-5 text-blue-600" />
                <span className="text-blue-800">
                  Great work! You're in the top 10 students in your class.
                </span>
              </div>
            )}
            
            {rankings.subjects && rankings.subjects.filter(s => s.rank <= 5).length > 0 && (
              <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <Award className="w-5 h-5 text-yellow-600" />
                <span className="text-yellow-800">
                  You have {rankings.subjects.filter(s => s.rank <= 5).length} subject{rankings.subjects.filter(s => s.rank <= 5).length !== 1 ? 's' : ''} in the top 5.
                </span>
              </div>
            )}
            
            {rankings.subjects && rankings.subjects.some(s => s.rank > 20) && (
              <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <Target className="w-5 h-5 text-orange-600" />
                <span className="text-orange-800">
                  Consider focusing on subjects where you can improve your ranking.
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RankingDisplay;
